export type LhuStyleOverride = {
  fontSize?: number;
  fontFamily?: string;
  fontWeight?: string;
  fontStyle?: string;
  textDecoration?: string;
  textTransform?: string;
  textAlign?: string;
  color?: string;
};

export type LhuStyleOverrideKey = keyof LhuStyleOverride;

/** target key → override, for one page format */
export type LhuPageStyleOverrides = Record<string, LhuStyleOverride>;

/** Keys and selectors must match `LHU_PAGE_FORMAT_SELECTORS` in backend `style-overrides.ts`. */
export const LHU_PAGE_FORMATS = [
  { key: 'cover', label: 'Cover', selector: '.page[data-lhu-slot="cover"]' },
  { key: 'letter', label: 'Surat', selector: '.page[data-lhu-slot="letter"]' },
  { key: 'sample-info', label: 'Informasi sampel', selector: '.page[data-lhu-slot="sample-info"]' },
  { key: 'result', label: 'Hasil uji (Summary of test result)', selector: '.page[data-lhu-slot^="result:"]' },
  { key: 'attachment', label: 'Attachment', selector: '.page[data-lhu-slot^="attachment:"]' },
] as const;

export type LhuPageFormat = (typeof LHU_PAGE_FORMATS)[number]['key'];

const FORMAT_BY_KEY = new Map<string, (typeof LHU_PAGE_FORMATS)[number]>(
  LHU_PAGE_FORMATS.map((f) => [f.key, f]),
);

/** Accepts a format key or a page slot key (older rows stored overrides per slot). */
export function lhuPageFormatOf(key: string): LhuPageFormat | null {
  if (FORMAT_BY_KEY.has(key)) return key as LhuPageFormat;
  if (key.startsWith('result:')) return 'result';
  if (key.startsWith('attachment:')) return 'attachment';
  return null;
}

/** page format → target key → override */
export type LhuStyleOverrideMap = Partial<Record<LhuPageFormat, LhuPageStyleOverrides>>;

export type LhuStyleTarget = {
  key: string;
  label: string;
  selectors: string[];
};

/** Keys and selectors must match `LHU_STYLE_TARGET_SELECTORS` in backend `style-overrides.ts`. */
export const LHU_STYLE_TARGETS: LhuStyleTarget[] = [
  { key: 'cover_title', label: 'Judul cover', selectors: ['.cover-title'] },
  { key: 'cover_meta', label: 'Tanggal & job number cover', selectors: ['.cover-meta-line'] },
  { key: 'cover_label', label: 'Label field cover', selectors: ['.cover-field .lbl'] },
  { key: 'cover_value', label: 'Nilai field cover', selectors: ['.cover-field .val:not(.attn)'] },
  { key: 'cover_attention', label: 'Attention cover', selectors: ['.cover-field .val.attn'] },
  { key: 'cover_banner', label: 'Teks banner hijau', selectors: ['.cover-banner-text p'] },
  { key: 'cover_banner_customer', label: 'Customer di banner', selectors: ['.cover-banner-text strong'] },
  { key: 'cover_disclaimer', label: 'Disclaimer cover', selectors: ['.cover-disclaimer'] },
  { key: 'letter_title', label: 'Judul surat', selectors: ['.letter-title'] },
  { key: 'section_title', label: 'Judul halaman', selectors: ['h2.section'] },
  { key: 'kv_label', label: 'Label info (kiri)', selectors: ['.kv .k'] },
  { key: 'kv_value', label: 'Nilai info (kanan)', selectors: ['.kv > span:last-child'] },
  { key: 'sign_block', label: 'Blok tanda tangan', selectors: ['.sign-block'] },
  { key: 'legal', label: 'Paragraf legal', selectors: ['.legal'] },
  { key: 'table_head', label: 'Header tabel', selectors: ['table.data th'] },
  { key: 'table_cell', label: 'Isi tabel', selectors: ['table.data td'] },
  { key: 'env_title', label: 'Judul Environmental Condition', selectors: ['.env-title'] },
  { key: 'remarks', label: 'Remarks', selectors: ['.remarks'] },
  { key: 'attach_sub', label: 'Job & customer attachment', selectors: ['.attach-sub'] },
  { key: 'attach_card_title', label: 'Judul sampling point', selectors: ['.attach-card h3'] },
  { key: 'attach_info', label: 'Info sampel attachment', selectors: ['.attach-info p', '.attach-info span'] },
  { key: 'doc_foot', label: 'Footer dokumen', selectors: ['.doc-foot'] },
];

const TARGET_BY_KEY = new Map(LHU_STYLE_TARGETS.map((t) => [t.key, t]));

export const FONT_FAMILY_OPTIONS: { value: string; label: string; css: string }[] = [
  { value: 'arial', label: 'Arial', css: 'Arial, Helvetica, sans-serif' },
  { value: 'times', label: 'Times New Roman', css: '"Times New Roman", Times, serif' },
  { value: 'courier', label: 'Courier New', css: '"Courier New", Courier, monospace' },
];

export const STYLE_ENUM_OPTIONS = {
  fontWeight: [
    { value: 'normal', label: 'Normal' },
    { value: 'bold', label: 'Bold' },
  ],
  fontStyle: [
    { value: 'normal', label: 'Normal' },
    { value: 'italic', label: 'Italic' },
  ],
  textDecoration: [
    { value: 'none', label: 'Tidak ada' },
    { value: 'underline', label: 'Underline' },
    { value: 'line-through', label: 'Coret' },
  ],
  textTransform: [
    { value: 'none', label: 'Normal' },
    { value: 'uppercase', label: 'HURUF BESAR' },
    { value: 'lowercase', label: 'huruf kecil' },
    { value: 'capitalize', label: 'Kapital Awal' },
  ],
  textAlign: [
    { value: 'left', label: 'Kiri' },
    { value: 'center', label: 'Tengah' },
    { value: 'right', label: 'Kanan' },
    { value: 'justify', label: 'Rata kiri-kanan' },
  ],
} as const;

type EnumKey = keyof typeof STYLE_ENUM_OPTIONS;
const ENUM_KEYS = Object.keys(STYLE_ENUM_OPTIONS) as EnumKey[];

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

export function sanitizeStyleOverride(input: unknown): LhuStyleOverride | null {
  const rec = asRecord(input);
  if (!rec) return null;
  const out: LhuStyleOverride = {};
  const size = Number(rec.fontSize);
  if (rec.fontSize !== undefined && rec.fontSize !== '' && Number.isFinite(size) && size >= 4 && size <= 96) {
    out.fontSize = Math.round(size * 2) / 2;
  }
  if (typeof rec.fontFamily === 'string' && FONT_FAMILY_OPTIONS.some((f) => f.value === rec.fontFamily)) {
    out.fontFamily = rec.fontFamily;
  }
  for (const key of ENUM_KEYS) {
    const value = rec[key];
    if (typeof value === 'string' && STYLE_ENUM_OPTIONS[key].some((o) => o.value === value)) out[key] = value;
  }
  if (typeof rec.color === 'string' && /^#[0-9a-fA-F]{6}$/.test(rec.color)) out.color = rec.color.toLowerCase();
  return Object.keys(out).length ? out : null;
}

export function sanitizePageStyleOverrides(input: unknown): LhuPageStyleOverrides {
  const rec = asRecord(input);
  if (!rec) return {};
  const out: LhuPageStyleOverrides = {};
  for (const [target, value] of Object.entries(rec)) {
    if (!TARGET_BY_KEY.has(target)) continue;
    const clean = sanitizeStyleOverride(value);
    if (clean) out[target] = clean;
  }
  return out;
}

export function sanitizeStyleOverrideMap(input: unknown): LhuStyleOverrideMap {
  const rec = asRecord(input);
  if (!rec) return {};
  const out: LhuStyleOverrideMap = {};
  for (const [key, targets] of Object.entries(rec)) {
    const format = lhuPageFormatOf(key);
    if (!format || out[format]) continue;
    const clean = sanitizePageStyleOverrides(targets);
    if (Object.keys(clean).length) out[format] = clean;
  }
  return out;
}

export function countStyleOverrides(map: LhuStyleOverrideMap): number {
  return Object.values(map).reduce((sum, targets) => sum + Object.keys(targets ?? {}).length, 0);
}

export function parseStoredStyles(raw: string): LhuStyleOverrideMap {
  const text = raw.trim();
  if (!text.startsWith('{')) return {};
  try {
    return sanitizeStyleOverrideMap((JSON.parse(text) as { styles?: unknown }).styles);
  } catch {
    return {};
  }
}

function declarations(override: LhuStyleOverride): string[] {
  const out: string[] = [];
  if (override.fontSize != null) out.push(`font-size: ${override.fontSize}px`);
  if (override.fontFamily) {
    const css = FONT_FAMILY_OPTIONS.find((f) => f.value === override.fontFamily)?.css;
    if (css) out.push(`font-family: ${css}`);
  }
  if (override.fontWeight) out.push(`font-weight: ${override.fontWeight}`);
  if (override.fontStyle) out.push(`font-style: ${override.fontStyle}`);
  if (override.textDecoration) out.push(`text-decoration: ${override.textDecoration}`);
  if (override.textTransform) out.push(`text-transform: ${override.textTransform}`);
  if (override.textAlign) out.push(`text-align: ${override.textAlign}`);
  if (override.color) out.push(`color: ${override.color}`);
  return out;
}

export function buildLhuStyleOverrideCss(map: LhuStyleOverrideMap): string {
  const rules: string[] = [];
  for (const [format, targets] of Object.entries(map)) {
    const pageSel = FORMAT_BY_KEY.get(format)?.selector;
    if (!pageSel || !targets) continue;
    for (const [targetKey, override] of Object.entries(targets)) {
      const target = TARGET_BY_KEY.get(targetKey);
      const decls = declarations(override);
      if (!target || !decls.length) continue;
      const scoped = target.selectors.map((sel) => `${pageSel} ${sel}`).join(',\n');
      rules.push(`${scoped} {\n  ${decls.map((d) => `${d} !important`).join(';\n  ')};\n}`);
    }
  }
  return rules.join('\n');
}

export type LhuStyleDefaults = Required<Omit<LhuStyleOverride, 'fontSize'>> & { fontSize: number };

export type LhuStyleTargetOnPage = LhuStyleTarget & { defaults: LhuStyleDefaults };

function rgbToHex(value: string): string {
  const m = /rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/i.exec(value);
  if (!m) return '#000000';
  return `#${[m[1], m[2], m[3]].map((n) => Number(n).toString(16).padStart(2, '0')).join('')}`;
}

function familyKey(value: string): string {
  const v = value.toLowerCase();
  if (v.includes('times')) return 'times';
  if (v.includes('courier')) return 'courier';
  return 'arial';
}

function pickEnum(key: EnumKey, value: string, fallback: string): string {
  return STYLE_ENUM_OPTIONS[key].some((o) => o.value === value) ? value : fallback;
}

/** Read computed styles of targets found on any of `pages`; caller disables override CSS first. */
export function readLhuStyleDefaults(pages: Element[]): LhuStyleTargetOnPage[] {
  const out: LhuStyleTargetOnPage[] = [];
  for (const target of LHU_STYLE_TARGETS) {
    const el = pages
      .flatMap((page) => target.selectors.map((sel) => page.querySelector(sel)))
      .find((node): node is Element => node instanceof Element);
    if (!el) continue;
    const cs = getComputedStyle(el);
    const weight = Number(cs.fontWeight);
    const decoration = (cs.textDecorationLine || cs.textDecoration || 'none').split(' ')[0];
    const align = cs.textAlign === 'start' ? 'left' : cs.textAlign === 'end' ? 'right' : cs.textAlign;
    out.push({
      ...target,
      defaults: {
        fontSize: Math.round(parseFloat(cs.fontSize) * 2) / 2 || 10,
        fontFamily: familyKey(cs.fontFamily),
        fontWeight: (Number.isFinite(weight) ? weight >= 600 : cs.fontWeight === 'bold') ? 'bold' : 'normal',
        fontStyle: cs.fontStyle === 'italic' || cs.fontStyle === 'oblique' ? 'italic' : 'normal',
        textDecoration: pickEnum('textDecoration', decoration, 'none'),
        textTransform: pickEnum('textTransform', cs.textTransform, 'none'),
        textAlign: pickEnum('textAlign', align, 'left'),
        color: rgbToHex(cs.color),
      },
    });
  }
  return out;
}
